// SiteCaptureScreen — deep-link landing for site-level infrastructure
// capture ("1 asset / site" assets). Per buildspec 20-ui §1.1 the
// initial load is a safe read: we read site metadata + the caller's
// active session (if any) but never create one as a side effect of
// route entry. Session creation is gesture-gated.
//
// Three render branches (20-ui §1.1, §1.1a, §5.4b):
//   1. session !== null → renders siteItems via <AssetRow>. Each
//      AssetRow dispatches through the shared captureClient so
//      mutations are coalesced at 500ms and written via PUT
//      /capture/items/:id.
//   2. session === null + field_staff → <StartCaptureCard scope="site">
//      with a real handleStart() that POSTs /capture/sessions then
//      reloads the screen via reloadKey.
//   3. session === null + admin → Admin Preview banner; read-only.
//
// Read-path migration (Phase D-2): site metadata, active session,
// and siteItems all come from the local PowerSync SQLite mirror via
// `useSite`, `useActiveSession`, and `useSiteItems`. The session-
// creation write now dispatches through the capture client (PowerSync-
// routable when the offline-write flag is on) so Start capture
// resolves offline instead of failing with a TypeError fetch error.

import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from 'react'
import { Check, X } from 'lucide-react'
import { useNavigate, useParams } from 'react-router'

import {
  type CaptureItemRow,
  type SessionSummary,
} from '@citycare/shared'

import { useAuth } from '../../app/auth-provider'
import { ApiError } from '../../hooks/api'
import { useActiveSession } from '../../hooks/powersync/use-active-session'
import { useSite } from '../../hooks/powersync/use-site'
import { useSiteItems } from '../../hooks/powersync/use-site-items'
import { Alert } from '../../ui/alert'
import { Button } from '../../ui/button'
import { Spinner } from '../../ui/spinner'

import { AssetRow, type AssetRowPatch } from '../components/asset-row'
import { CancelSessionDialog } from '../components/cancel-session-dialog'
import { CaptureHero } from '../components/capture-hero'
import { CaptureLayout } from '../components/capture-layout'
import { type SiteSessionStatus } from '../components/site-status-chip'
import { StartCaptureCard } from '../components/start-capture-card'
import { StickyBottomBar } from '../components/sticky-bottom-bar'
import { useRegisteredCaptureClient } from '../client/use-registered-capture-client'

import { type PendingCreate } from './room-capture-reconcile'
import { makeSiteItemUpdate } from './site-capture-update'
import { useCancelSession } from './use-cancel-session'

interface SiteMetadata {
  id: string
  name: string
}

function describeFetchError(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'Unknown error'
}

export function SiteCaptureScreen(): ReactElement {
  const { siteId = '' } = useParams<{ siteId: string }>()
  const { user, status } = useAuth()
  const siteResult = useSite(siteId)
  const activeSessionResult = useActiveSession(
    siteId === '' ? null : { siteId },
  )

  // Optimistic bridges createSession-POST → sync; ackedOptimisticId
  // ensures the fallback stops once the synced query catches up so
  // post-completion null returns from useActiveSession don't stick on
  // a spinner (Codex P1 on PR #511 / PR #507).
  const [optimisticSession, setOptimisticSession] =
    useState<SessionSummary | null>(null)
  const [ackedOptimisticId, setAckedOptimisticId] =
    useState<string | null>(null)
  if (
    optimisticSession !== null &&
    activeSessionResult.session?.id === optimisticSession.id &&
    ackedOptimisticId !== optimisticSession.id
  ) {
    setAckedOptimisticId(optimisticSession.id)
  }
  const optimisticIsLive =
    optimisticSession !== null &&
    ackedOptimisticId !== optimisticSession.id
  const effectiveSession =
    activeSessionResult.session ?? (optimisticIsLive ? optimisticSession : null)
  const sessionIdForItems = effectiveSession?.id ?? ''
  const itemsResult = useSiteItems(sessionIdForItems)
  const optimisticPendingSync =
    optimisticIsLive && activeSessionResult.session === null
  const authPending = status === 'loading' || user === undefined
  const dataPending =
    siteResult.isLoading ||
    activeSessionResult.isLoading ||
    optimisticPendingSync ||
    (activeSessionResult.session !== null && itemsResult.isLoading)
  const errorMessage =
    siteResult.error?.message ??
    activeSessionResult.error?.message ??
    itemsResult.error?.message ??
    null

  const site: SiteMetadata | null = siteResult.site
    ? { id: siteResult.site.id, name: siteResult.site.name }
    : null

  return (
    <CaptureLayout
      breadcrumb={{
        // Parent of the site-capture screen is the site detail page.
        // siteId comes from the URL param so this resolves even before
        // the site detail has loaded — required by CaptureBreadcrumb
        // after PR #452 Codex review P2.
        parentTo: `/capture/sites/${encodeURIComponent(siteId)}`,
        site: { label: site?.name ?? 'Site' },
      }}
    >
      <section
        className="mx-auto flex w-full max-w-2xl flex-col gap-4 pb-safe"
        aria-label="Site-level capture"
      >
        {/* Visually hidden page heading. The CaptureHero carries the
             site name as an h2; this h1 keeps the routing-smoke tests'
             heading lookup working. */}
        <h1 className="sr-only">Site capture</h1>
        {errorMessage !== null ? (
          <Alert variant="error" title="Could not load this site">
            {errorMessage}
          </Alert>
        ) : dataPending || authPending || site === null ? (
          <Spinner label="Loading capture workspace…" size="md" />
        ) : (
          <SiteCaptureContent
            site={site}
            session={effectiveSession}
            siteItems={itemsResult.siteItems}
            role={user.role}
            userId={user.userID}
            userDisplayName={user.displayName}
            onSessionCreated={setOptimisticSession}
          />
        )}
      </section>
    </CaptureLayout>
  )
}

interface SiteCaptureContentProps {
  site: SiteMetadata
  session: SessionSummary | null
  siteItems: CaptureItemRow[]
  role: 'field_staff' | 'admin'
  userId: string
  userDisplayName: string
  onSessionCreated: (session: SessionSummary) => void
}

function SiteCaptureContent({
  site,
  session,
  siteItems,
  role,
  userId,
  userDisplayName,
  onSessionCreated,
}: SiteCaptureContentProps): ReactElement {
  const navigate = useNavigate()
  const [isStarting, setIsStarting] = useState(false)
  const [startError, setStartError] = useState<string | null>(null)
  // Optimistic status for the complete/reopen chip; syncVersion
  // tracks the latest from dispatch so the next toggle isn't stale.
  const [sessionStatus, setSessionStatus] = useState<SiteSessionStatus>(
    session?.completedAt != null ? 'complete' : 'in_progress',
  )
  const [currentSyncVersion, setCurrentSyncVersion] = useState<number>(
    session?.syncVersion ?? 1,
  )

  // One capture client per mounted screen (same 500ms coalesce as
  // room-capture-screen.tsx). The hook creates it, registers it for
  // logout-flush (#726), and tears down pending timers on unmount.
  const captureClient = useRegisteredCaptureClient()

  const cancelDialog = useCancelSession({
    captureClient,
    sessionId: session?.id ?? null,
    syncVersion: currentSyncVersion,
  })
  const onConfirmCancel = useCallback(async () => {
    const result = await cancelDialog.confirm()
    if (result === null) return
    void navigate(`/capture/sites/${encodeURIComponent(site.id)}`)
  }, [cancelDialog, navigate, site.id])

  // Optimistic overrides keyed by assetTypeId (NOT capture_items.id):
  // template-only rows synthesised by useSiteItems share id '', so a row
  // must be addressed by its asset type, which is unique per site session
  // (persisted OR template-only, never both). Each entry shadows the
  // corresponding row from the hook's snapshot until the authoritative
  // response from `captureClient.dispatch` lands and we replace the
  // override with the server's row. Keeping the override layer separate
  // avoids the React anti-pattern of mirroring a prop in useState — the
  // prop stays the source of truth, the override is the in-flight tween.
  const [optimisticOverrides, setOptimisticOverrides] = useState<
    ReadonlyMap<string, CaptureItemRow>
  >(() => new Map())
  // In-flight createItem tracker for template-only rows: assetTypeId →
  // the user's latest optimistic values. Guards against duplicate INSERTs
  // on press-and-hold (see makeSiteItemUpdate / site-capture-update.ts).
  const pendingCreatesRef = useRef<Map<string, PendingCreate>>(new Map())
  const effectiveSiteItems = useMemo(
    () =>
      siteItems.map(
        (item) => optimisticOverrides.get(item.assetTypeId) ?? item,
      ),
    [siteItems, optimisticOverrides],
  )

  // Gesture-gated session creation. Field_staff only. Dispatches
  // through captureClient so the PowerSync adapter (when the
  // offline-write flag is on) can land the session in local SQLite
  // and queue the POST for upload. After success we hand the freshly-
  // minted session up to the screen so the StartCaptureCard flips to
  // the in-session view immediately, without waiting for the next
  // captures sync tick.
  const handleStart = useCallback(() => {
    setIsStarting(true)
    setStartError(null)
    captureClient
      .dispatch({
        kind: 'createSession',
        siteId: site.id,
        buildingId: null,
        userId,
        userDisplayName,
      })
      .then((result) => {
        setIsStarting(false)
        if (result.kind !== 'createSession') {
          throw new Error(
            `unexpected dispatch result kind ${result.kind} for createSession`,
          )
        }
        onSessionCreated(result.session as SessionSummary)
      })
      .catch((err: unknown) => {
        setIsStarting(false)
        setStartError(describeFetchError(err))
      })
  }, [site.id, onSessionCreated, captureClient, userId, userDisplayName])

  // Complete / reopen toggle for site-scoped sessions.
  const handleStatusToggle = useCallback((): void => {
    if (session === null) return
    const isCurrentlyComplete = sessionStatus === 'complete'
    const nextStatus: SiteSessionStatus = isCurrentlyComplete
      ? 'in_progress'
      : 'complete'

    void captureClient
      .dispatch(
        isCurrentlyComplete
          ? {
              kind: 'reopenSession',
              sessionId: session.id,
              syncVersion: currentSyncVersion,
            }
          : {
              kind: 'completeSession',
              sessionId: session.id,
              syncVersion: currentSyncVersion,
            },
      )
      .then((result) => {
        if (
          result.kind === 'completeSession' ||
          result.kind === 'reopenSession'
        ) {
          const updated = result.session as SessionSummary
          setCurrentSyncVersion(updated.syncVersion)
        }
        setSessionStatus(nextStatus)
      })
      .catch(() => {
        // Permanent failures emit via the capture event bus. The
        // optimistic status is NOT rolled back — LWW semantics.
      })
  }, [captureClient, session, sessionStatus, currentSyncVersion])

  // Per-row update handler. Optimistic overrides + authoritative echoes
  // flow through the override map (keyed by assetTypeId) so the hook's
  // `siteItems` prop stays untouched. Template-only rows take the
  // createItem path on first write; persisted rows take updateItem. The
  // create-vs-update branching, duplicate-create guard, and echo
  // reconciliation live in makeSiteItemUpdate (site-capture-update.ts).
  // `pendingCreatesRef.current` is read at tap time (event), not render.
  const makeHandleUpdate = useCallback(
    (assetTypeId: string) =>
      (patch: AssetRowPatch): void => {
        makeSiteItemUpdate({
          captureClient,
          sessionId: session?.id ?? '',
          items: effectiveSiteItems,
          setOverrides: setOptimisticOverrides,
          pendingCreates: pendingCreatesRef.current,
        })(assetTypeId, patch)
      },
    [captureClient, session, effectiveSiteItems],
  )

  if (session !== null) {
    const totalCount = effectiveSiteItems.length
    const doneCount = effectiveSiteItems.filter(
      (i) => parseFloat(i.quantity) > 0 || i.isZeroConfirmed,
    ).length
    const heroCountLine =
      totalCount > 0
        ? `${doneCount}/${totalCount} assets captured`
        : null

    const handleCancel = (): void => {
      // "Cancel" returns to the site detail page without closing
      // the session — autosave keeps the values intact, the user
      // can resume by re-entering site-level capture.
      void navigate(`/capture/sites/${encodeURIComponent(site.id)}`)
    }
    const isComplete = sessionStatus === 'complete'

    return (
      <section
        className="flex flex-col gap-4"
        aria-label={`Capture ${site.name}`}
      >
        <CaptureHero
          eyebrow="Site-level capture"
          name={site.name}
          subtitle="Shared site infrastructure"
          countLine={heroCountLine}
        />
        {effectiveSiteItems.length === 0 ? (
          <p className="rounded-2xl bg-white p-5 text-sm text-[color:var(--color-cc-charcoal)]/70 shadow-sm">
            No site-level assets are configured. Ask an admin to tag assets as
            &lsquo;1 asset / site&rsquo; in the classification manager.
          </p>
        ) : (
          <>
            <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-[color:var(--color-cc-charcoal)]/55">
              Site infrastructure
            </span>
            <ul
              className="overflow-hidden rounded-2xl border border-[color:var(--color-cc-forest)]/10 bg-white shadow-sm"
              aria-label="Site-level assets"
            >
              {effectiveSiteItems.map((item, idx) => (
                <li
                  key={item.assetTypeId}
                  className={
                    idx > 0
                      ? 'border-t border-[color:var(--color-cc-forest)]/10'
                      : ''
                  }
                >
                  <AssetRow
                    item={item}
                    onUpdate={makeHandleUpdate(item.assetTypeId)}
                  />
                </li>
              ))}
            </ul>
          </>
        )}
        <StickyBottomBar>
          <Button
            variant="ghost"
            onClick={handleCancel}
            aria-label="Cancel site capture"
            className="flex-1 min-h-12 justify-center rounded-2xl border border-[color:var(--color-cc-forest)]/15 bg-white text-[color:var(--color-cc-forest)] hover:bg-[color:var(--color-cc-cream)]"
          >
            <X aria-hidden="true" className="h-4 w-4" />
            Back
          </Button>
          {!isComplete ? (
            <Button
              variant="ghost"
              onClick={cancelDialog.open}
              disabled={cancelDialog.isSubmitting}
              aria-label="Cancel site capture session"
              data-testid="cancel-site-session-btn"
              className="min-h-12 justify-center rounded-2xl border border-red-200 bg-white text-red-600 hover:bg-red-50"
            >
              Cancel session
            </Button>
          ) : null}
          <Button
            variant="primary"
            onClick={handleStatusToggle}
            aria-label={
              isComplete ? 'Reopen site capture' : 'Save site capture'
            }
            data-testid={
              isComplete ? 'reopen-site-btn' : 'mark-site-complete-btn'
            }
            className="flex-1 min-h-12 justify-center gap-1.5 rounded-2xl bg-[color:var(--color-cc-forest)] text-[color:var(--color-cc-lime)] hover:bg-[color:var(--color-cc-forest)]/90"
          >
            {isComplete ? 'Reopen site capture' : 'Save site capture'}
            {isComplete ? null : (
              <Check aria-hidden="true" className="h-4 w-4" />
            )}
          </Button>
        </StickyBottomBar>

        <CancelSessionDialog
          open={cancelDialog.isOpen}
          scope="site"
          scopeName={site.name}
          submitting={cancelDialog.isSubmitting}
          error={cancelDialog.error}
          onClose={cancelDialog.close}
          onConfirm={() => void onConfirmCancel()}
        />
      </section>
    )
  }

  if (role === 'admin') {
    return (
      <section
        className="space-y-3"
        aria-labelledby="admin-preview-site-heading"
      >
        <Alert variant="info" title="Admin preview">
          Admin preview — you are not capturing this site.
        </Alert>
        <header className="rounded-2xl border border-[color:var(--color-cc-forest)]/10 bg-white p-5 shadow-sm">
          <h2
            id="admin-preview-site-heading"
            className="font-display text-2xl font-semibold text-[color:var(--color-cc-forest)]"
          >
            {site.name}
          </h2>
        </header>
      </section>
    )
  }

  return (
    <>
      {startError !== null ? (
        <Alert variant="error" title="Could not start capture session">
          {startError}
        </Alert>
      ) : null}
      <StartCaptureCard
        buildingName={site.name}
        buildingFloc={null}
        scope="site"
        isStarting={isStarting}
        onStart={handleStart}
      />
    </>
  )
}
